import React, { useState, useEffect } from 'react';
import { predictionsAPI } from '../../services/api';
import '../../components/Layout/Layout.css';

const PROVIDER_COLORS = { claude:'#ff6b35', openai:'#00a67e', deepseek:'#4d9de0' };
const SIGNAL_COLORS   = { STRONG_BUY:'#00ff88', BUY:'#00cc6a', HOLD:'#ffb800', SELL:'#ff6b6b', STRONG_SELL:'#ff4d4d' };
const MARKET_FLAGS    = { india:'🇮🇳', us:'🇺🇸', crypto:'₿', global:'🌍' };

const AccuracyBar = ({ value, color='#00ff88' }) => (
  <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:'4px', height:'8px', width:'100%' }}>
    <div style={{ height:'100%', borderRadius:'4px', background:color, width:`${Math.min(100,value||0)}%`, transition:'width 0.5s' }}></div>
  </div>
);

export default function AccuracyPage() {
  const [stats,     setStats]     = useState(null);
  const [history,   setHistory]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [checking,  setChecking]  = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [message,   setMessage]   = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        predictionsAPI.getAccuracyStats(),
        predictionsAPI.getHistory(),
      ]);
      setStats(s);
      setHistory(h);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const checkPredictions = async () => {
    setChecking(true); setMessage('');
    try {
      const r = await predictionsAPI.checkPredictions();
      setMessage(`✅ Checked ${r.checked} predictions — ${r.correct} correct, ${r.incorrect} incorrect`);
      await loadData();
    } catch(e) { setMessage(`❌ ${e.response?.data?.detail||e.message}`); }
    finally { setChecking(false); }
  };

  const adjustWeights = async () => {
    setAdjusting(true); setMessage('');
    try {
      const r = await predictionsAPI.autoAdjustWeights();
      if (r.adjusted) {
        setMessage(`✅ Weights adjusted! ${r.adjustments.map(a=>`${a.provider}: ${a.old_weight}→${a.new_weight} (${a.accuracy}%)`).join(', ')}`);
      } else {
        setMessage(`ℹ️ ${r.message}`);
      }
      await loadData();
    } catch(e) { setMessage(`❌ ${e.response?.data?.detail||e.message}`); }
    finally { setAdjusting(false); }
  };

  return (
    <div className="page">
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div className="page-title">🎯 AI Prediction Accuracy</div>
          <div className="page-subtitle">Track AI accuracy and auto-adjust provider weights</div>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button className="btn btn-outline" onClick={checkPredictions} disabled={checking}>
            {checking ? '🔄 Checking...' : '🔍 Check Predictions'}
          </button>
          <button className="btn btn-primary" onClick={adjustWeights} disabled={adjusting}>
            {adjusting ? '⚙️ Adjusting...' : '⚡ Auto-Adjust Weights'}
          </button>
        </div>
      </div>

      {message && (
        <div style={{ padding:'12px 16px', borderRadius:'10px', marginBottom:'20px', fontSize:'14px',
          background: message.startsWith('✅')?'rgba(0,255,136,0.1)':message.startsWith('ℹ️')?'rgba(0,184,255,0.1)':'rgba(255,77,77,0.1)',
          border:`1px solid ${message.startsWith('✅')?'rgba(0,255,136,0.3)':message.startsWith('ℹ️')?'rgba(0,184,255,0.3)':'rgba(255,77,77,0.3)'}`,
          color: message.startsWith('✅')?'#00ff88':message.startsWith('ℹ️')?'#00b8ff':'#ff4d4d',
        }}>{message}</div>
      )}

      {loading && <div className="loading"><div className="spinner"></div>Loading...</div>}

      {!loading && stats && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'24px' }}>
            {[
              {label:'Total Predictions', value:stats.total||0,        color:'#fff'},
              {label:'Correct',           value:stats.correct||0,      color:'#00ff88'},
              {label:'Overall Accuracy',  value:`${stats.accuracy||0}%`, color:stats.accuracy>=60?'#00ff88':stats.accuracy>=40?'#ffb800':'#ff4d4d'},
              {label:'Providers',         value:Object.keys(stats.by_provider||{}).length, color:'#00b8ff'},
            ].map((s,i) => (
              <div key={i} className="stat-card" style={{ padding:'16px' }}>
                <div className="stat-label">{s.label}</div>
                <div className="stat-value" style={{ fontSize:'24px', color:s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div className="card">
            <div style={{ display:'flex', gap:'4px', background:'rgba(255,255,255,0.05)', padding:'4px', borderRadius:'10px', marginBottom:'20px', width:'fit-content' }}>
              {['overview','providers','markets','signals','history'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  style={{ padding:'7px 16px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'600',
                    background:activeTab===tab?'#00ff88':'transparent', color:activeTab===tab?'#080c14':'rgba(255,255,255,0.5)' }}>
                  {tab.charAt(0).toUpperCase()+tab.slice(1)}
                </button>
              ))}
            </div>

            {activeTab==='overview' && (
              <div>
                {stats.total === 0 ? (
                  <div style={{ textAlign:'center', padding:'60px', color:'rgba(255,255,255,0.3)' }}>
                    <div style={{ fontSize:'48px', marginBottom:'16px' }}>🎯</div>
                    <div style={{ fontSize:'16px', marginBottom:'8px' }}>No predictions checked yet</div>
                    <div style={{ fontSize:'13px' }}>Analyze stocks → wait 24 hours → click "Check Predictions"</div>
                  </div>
                ) : (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'24px' }}>
                    <div>
                      <div style={{ fontWeight:'700', marginBottom:'14px' }}>By AI Provider</div>
                      {Object.entries(stats.by_provider||{}).map(([p, d]) => (
                        <div key={p} style={{ marginBottom:'14px' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px' }}>
                            <span style={{ color:PROVIDER_COLORS[p]||'#fff', fontWeight:'600', textTransform:'capitalize' }}>{p}</span>
                            <span style={{ color:d.accuracy>=60?'#00ff88':d.accuracy>=40?'#ffb800':'#ff4d4d', fontWeight:'700' }}>{d.accuracy}%</span>
                          </div>
                          <AccuracyBar value={d.accuracy} color={PROVIDER_COLORS[p]||'#00ff88'} />
                          <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)', marginTop:'3px' }}>{d.correct}/{d.total} correct</div>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div style={{ fontWeight:'700', marginBottom:'14px' }}>By Market</div>
                      {Object.entries(stats.by_market||{}).map(([m, d]) => (
                        <div key={m} style={{ marginBottom:'14px' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px' }}>
                            <span style={{ fontWeight:'600' }}>{MARKET_FLAGS[m]} {m.charAt(0).toUpperCase()+m.slice(1)}</span>
                            <span style={{ color:d.accuracy>=60?'#00ff88':d.accuracy>=40?'#ffb800':'#ff4d4d', fontWeight:'700' }}>{d.accuracy}%</span>
                          </div>
                          <AccuracyBar value={d.accuracy} />
                          <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)', marginTop:'3px' }}>{d.correct}/{d.total} correct</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab==='providers' && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'16px' }}>
                {Object.entries(stats.by_provider||{}).map(([p, d]) => (
                  <div key={p} style={{ padding:'20px', background:'rgba(255,255,255,0.03)', borderRadius:'12px', border:`1px solid ${PROVIDER_COLORS[p]||'#fff'}30` }}>
                    <div style={{ fontSize:'18px', fontWeight:'800', color:PROVIDER_COLORS[p]||'#fff', textTransform:'capitalize', marginBottom:'12px' }}>{p}</div>
                    <div style={{ fontSize:'36px', fontWeight:'800', color:d.accuracy>=60?'#00ff88':d.accuracy>=40?'#ffb800':'#ff4d4d', marginBottom:'8px' }}>{d.accuracy}%</div>
                    <AccuracyBar value={d.accuracy} color={PROVIDER_COLORS[p]||'#00ff88'} />
                    <div style={{ marginTop:'12px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                      <div style={{ padding:'8px', background:'rgba(0,255,136,0.05)', borderRadius:'8px', textAlign:'center' }}>
                        <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)' }}>Correct</div>
                        <div style={{ fontSize:'18px', fontWeight:'700', color:'#00ff88' }}>{d.correct}</div>
                      </div>
                      <div style={{ padding:'8px', background:'rgba(255,255,255,0.03)', borderRadius:'8px', textAlign:'center' }}>
                        <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)' }}>Total</div>
                        <div style={{ fontSize:'18px', fontWeight:'700' }}>{d.total}</div>
                      </div>
                    </div>
                  </div>
                ))}
                {Object.keys(stats.by_provider||{}).length===0 && <div style={{ textAlign:'center', padding:'40px', color:'rgba(255,255,255,0.3)', gridColumn:'span 3' }}>No provider data yet.</div>}
              </div>
            )}

            {activeTab==='markets' && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'16px' }}>
                {Object.entries(stats.by_market||{}).map(([m, d]) => (
                  <div key={m} style={{ padding:'20px', background:'rgba(255,255,255,0.03)', borderRadius:'12px', border:'1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ fontSize:'28px', marginBottom:'8px' }}>{MARKET_FLAGS[m]}</div>
                    <div style={{ fontSize:'18px', fontWeight:'700', marginBottom:'12px' }}>{m.charAt(0).toUpperCase()+m.slice(1)}</div>
                    <div style={{ fontSize:'32px', fontWeight:'800', color:d.accuracy>=60?'#00ff88':d.accuracy>=40?'#ffb800':'#ff4d4d', marginBottom:'8px' }}>{d.accuracy}%</div>
                    <AccuracyBar value={d.accuracy} />
                    <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.4)', marginTop:'8px' }}>{d.correct} correct out of {d.total}</div>
                  </div>
                ))}
                {Object.keys(stats.by_market||{}).length===0 && <div style={{ textAlign:'center', padding:'40px', color:'rgba(255,255,255,0.3)', gridColumn:'span 2' }}>No market data yet.</div>}
              </div>
            )}

            {activeTab==='signals' && (
              <div>
                {Object.entries(stats.by_signal||{}).map(([sig, d]) => (
                  <div key={sig} style={{ display:'flex', alignItems:'center', gap:'16px', padding:'14px', background:'rgba(255,255,255,0.03)', borderRadius:'10px', marginBottom:'10px' }}>
                    <span style={{ padding:'4px 12px', borderRadius:'20px', fontWeight:'700', fontSize:'12px', background:`${SIGNAL_COLORS[sig]||'#fff'}15`, color:SIGNAL_COLORS[sig]||'#fff', minWidth:'100px', textAlign:'center' }}>{sig}</span>
                    <div style={{ flex:1 }}><AccuracyBar value={d.accuracy} color={SIGNAL_COLORS[sig]||'#00ff88'} /></div>
                    <span style={{ fontWeight:'700', color:d.accuracy>=60?'#00ff88':d.accuracy>=40?'#ffb800':'#ff4d4d', minWidth:'50px', textAlign:'right' }}>{d.accuracy}%</span>
                    <span style={{ fontSize:'12px', color:'rgba(255,255,255,0.3)', minWidth:'80px', textAlign:'right' }}>{d.correct}/{d.total}</span>
                  </div>
                ))}
                {Object.keys(stats.by_signal||{}).length===0 && <div style={{ textAlign:'center', padding:'40px', color:'rgba(255,255,255,0.3)' }}>No signal data yet.</div>}
              </div>
            )}

            {activeTab==='history' && (
              <div className="table-wrapper">
                {history.length===0
                  ? <div style={{ textAlign:'center', padding:'40px', color:'rgba(255,255,255,0.3)' }}>No prediction history yet.</div>
                  : <table>
                      <thead><tr><th>Date</th><th>Symbol</th><th>Market</th><th>Signal</th><th>Entry</th><th>Target</th><th>Actual</th><th>Result</th></tr></thead>
                      <tbody>
                        {history.map(p => (
                          <tr key={p.id}>
                            <td style={{ fontSize:'12px' }}>{p.created_at?new Date(p.created_at).toLocaleDateString():'--'}</td>
                            <td style={{ fontWeight:'700' }}>{p.symbol}</td>
                            <td>{MARKET_FLAGS[p.market]} {p.market}</td>
                            <td><span style={{ color:SIGNAL_COLORS[p.final_signal]||'#fff', fontWeight:'700', fontSize:'12px' }}>{p.final_signal}</span></td>
                            <td>{p.current_price?`$${parseFloat(p.current_price).toFixed(2)}`:'--'}</td>
                            <td style={{ color:'#00ff88' }}>{p.target_price?`$${parseFloat(p.target_price).toFixed(2)}`:'--'}</td>
                            <td style={{ color:'#00b8ff' }}>{p.actual_price?`$${parseFloat(p.actual_price).toFixed(2)}`:'--'}</td>
                            <td>{p.was_correct===null||p.was_correct===undefined
                              ? <span style={{ color:'rgba(255,255,255,0.3)', fontSize:'12px' }}>Pending</span>
                              : p.was_correct
                                ? <span style={{ color:'#00ff88', fontWeight:'700' }}>✅ Correct</span>
                                : <span style={{ color:'#ff4d4d', fontWeight:'700' }}>❌ Wrong</span>
                            }</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                }
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
